import { lifetimeConfigParams, spatialTypeOfMovement } from '@hub/bearing-hub/common/constants';
import type {
  LifetimeConfigParams,
  LifetimeConfigTypeOfOperation,
  SpatialTypeOfMovement,
  TemporalTypeOfMovement,
} from '@hub/bearing-hub/common/types';
import { lifetimeConfigTypeOfOperation, temporalTypeOfMovement } from '@hub/bearing-hub/common/types';
import {
  DefaultServiceLifeForBearings,
  DefaultServiceLifeForBearingsForUS,
  getDefaultAcceptableWearServiceLife,
  getDefaultTemperatureServiceLife,
} from '@hub/bearing-hub/libs/BearingsLifetime/constants';
import type { ButtonGroupDefinition } from '@hub/common/ButtonGroup/ButtonGroup';
import type { UnitCode } from '@icommerce/units';
import {
  AngleCode,
  DurationCode,
  ForceCode,
  getUnitLabel,
  getUnitLabelForUnitSystem,
  LinearFrequencyCode,
  PressureCode,
  RotationalFrequencyCode,
  TemperatureCode,
} from '@icommerce/units';
import type { TFunction } from 'next-i18next';
import { UnitSystem } from '@igus/kopla-domain';
import {
  getIcommerceUnitSystemName,
  getLengthCode,
  getSpeedCode,
} from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';

export enum LifetimeParameterInputType {
  numberInput = 'numberInput',
  filterSelectAutocomplete = 'filterSelectAutocomplete',
  buttonGroup = 'buttonGroup',
  none = 'none',
}

export enum LifetimeParameterInputSectionType {
  environment = 'environment',
  materials = 'materials',
  operation = 'operation',
  motion = 'motion',
}

export type LifetimeParameterInput = {
  title: string;
  attribute: LifetimeConfigParams;
  attributeText: string;
  unit?: string;
  readOnly?: boolean;
  defaultValue: number | string;
  inputType: LifetimeParameterInputType;
  buttonGroupDefinition?: ButtonGroupDefinition<TemporalTypeOfMovement>[];
  hint?: string;
  hintImage?: string;
};

export type LifetimeParameterInputSection = {
  type: LifetimeParameterInputSectionType;
  title: string;
  items: {
    [key in LifetimeConfigTypeOfOperation | SpatialTypeOfMovement | 'default']?: LifetimeParameterInput[];
  };
};

export interface CreateInputsDefinitionsParams {
  t: TFunction;
  unitSystem: UnitSystem;
  locale: string;
  getSurfacePressureValue: (force: number) => number;
  getRotatingSpeedValue: () => number;
  getLinearSpeedValue: () => number;
  getPivotingSpeedValue: () => number;
}

export const createInputsDefinitions = ({
  t,
  unitSystem,
  locale,
  getSurfacePressureValue,
  getRotatingSpeedValue,
  getLinearSpeedValue,
  getPivotingSpeedValue,
}: CreateInputsDefinitionsParams): LifetimeParameterInputSection[] => {
  const temperatureText = unitSystem === UnitSystem.imperial ? '73.4 °F' : '23 °C';

  const temporalMovementButtonGroupDefinition: ButtonGroupDefinition<TemporalTypeOfMovement>[] = [
    {
      text: t('LIFETIME.ONE_AFTER_ANOTHER', { ns: 'bearing-hub/bearingHub' }),
      value: temporalTypeOfMovement.sequential,
    },
    {
      text: t('LIFETIME.AT_THE_SAME_TIME', { ns: 'bearing-hub/bearingHub' }),
      value: temporalTypeOfMovement.simultaneous,
    },
  ];

  return [
    {
      type: LifetimeParameterInputSectionType.environment,
      title: 'LIFETIME.SECTION_ENVIRONMENT',
      items: {
        default: [
          {
            title: 'LIFETIME.ENVIRONMENT',
            attribute: lifetimeConfigParams.environment,
            attributeText: 'LIFETIME.ENVIRONMENT',
            defaultValue: DefaultServiceLifeForBearings.environment,
            readOnly: true,
            inputType: LifetimeParameterInputType.none,
            hint: t(`LIFETIME.ENVIRONMENT_HINT`, {
              ns: 'bearing-hub/bearingHub',
              temperature: temperatureText,
            }),
          },
          {
            title: 'LIFETIME.TEMPERATURE',
            attribute: lifetimeConfigParams.temperature,
            attributeText: 'LIFETIME.TEMPERATURE',
            defaultValue: getDefaultTemperatureServiceLife(unitSystem),
            readOnly: true,
            unit: getUnitLabelForUnitSystem(TemperatureCode.CELSIUS, getIcommerceUnitSystemName(unitSystem)),
            inputType: LifetimeParameterInputType.none,
            hint: t(`LIFETIME.TEMPERATURE_HINT`, { ns: 'bearing-hub/bearingHub', temperature: temperatureText }),
          },
          {
            title: 'LIFETIME.LOAD',
            attribute: lifetimeConfigParams.force,
            attributeText: 'LIFETIME.LOAD',
            defaultValue:
              unitSystem === UnitSystem.metric
                ? (DefaultServiceLifeForBearings.load as { force: number }).force
                : DefaultServiceLifeForBearingsForUS.load.force,
            unit: getUnitLabelForUnitSystem(ForceCode.NEWTON, getIcommerceUnitSystemName(unitSystem)),
            inputType: LifetimeParameterInputType.numberInput,
          },
          {
            title: 'LIFETIME.SURFACE_PRESSURE',
            attribute: lifetimeConfigParams.surfacePressure,
            attributeText: 'LIFETIME.SURFACE_PRESSURE',
            defaultValue: getSurfacePressureValue(DefaultServiceLifeForBearingsForUS.load.force),
            unit: getUnitLabelForUnitSystem(PressureCode.PSI, getIcommerceUnitSystemName(unitSystem)),
            inputType: LifetimeParameterInputType.numberInput,
          },
          {
            title: 'LIFETIME.ACCEPTABLE_WEAR',
            attribute: lifetimeConfigParams.acceptableWear,
            attributeText: 'LIFETIME.ACCEPTABLE_WEAR',
            defaultValue: getDefaultAcceptableWearServiceLife(unitSystem),
            unit: getLengthCode(unitSystem),
            inputType: LifetimeParameterInputType.numberInput,
          },
        ],
      },
    },
    {
      type: LifetimeParameterInputSectionType.materials,
      title: 'LIFETIME.SECTION_MATERIALS',
      items: {
        default: [
          {
            title: '',
            attribute: lifetimeConfigParams.oppositeMaterialId,
            attributeText: '',
            defaultValue: DefaultServiceLifeForBearings.oppositeMaterialId,
            inputType: LifetimeParameterInputType.filterSelectAutocomplete,
          },
        ],
      },
    },
    {
      type: LifetimeParameterInputSectionType.operation,
      title: 'LIFETIME.SECTION_TYPE_OF_OPERATION',
      items: {
        [lifetimeConfigTypeOfOperation.continous]: [
          {
            title: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            attribute: lifetimeConfigParams.activityMinutesPerHour,
            attributeText: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            defaultValue: DefaultServiceLifeForBearings.activityMinutesPerHour,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.MINUTE),
          },
        ],
        [lifetimeConfigTypeOfOperation.intermittent]: [
          {
            title: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            attribute: lifetimeConfigParams.activityMinutesPerHour,
            attributeText: 'LIFETIME.ACTIVITY_MINUTES_PER_HOUR',
            defaultValue: DefaultServiceLifeForBearings.activityMinutesPerHour,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.MINUTE),
          },
          {
            title: 'LIFETIME.ON_TIME',
            attribute: lifetimeConfigParams.onTime,
            attributeText: 'LIFETIME.ON_TIME',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.SECOND),
          },
          {
            title: 'LIFETIME.OFF_TIME',
            attribute: lifetimeConfigParams.offTime,
            attributeText: 'LIFETIME.OFF_TIME',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(DurationCode.SECOND),
          },
        ],
      },
    },
    {
      type: LifetimeParameterInputSectionType.motion,
      title: 'LIFETIME.SECTION_TYPE_OF_MOTION',
      items: {
        [spatialTypeOfMovement.rotation]: [
          {
            title: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            hint: t(`LIFETIME.ROTATIONAL_REVOLUTIONS_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/rotation.png',
            attribute: lifetimeConfigParams.rotationRevolutions,
            attributeText: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            defaultValue: DefaultServiceLifeForBearings.typeOfMovement,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(
              (locale === 'en'
                ? RotationalFrequencyCode.ROTATIONAL_REVOLUTION_PER_MINUTE
                : RotationalFrequencyCode.REVOLUTION_PER_MINUTE) as UnitCode
            ),
          },
          {
            title: 'LIFETIME.ROTATING_SPEED',
            attribute: lifetimeConfigParams.rotatingSpeed,
            attributeText: 'LIFETIME.ROTATING_SPEED',
            defaultValue: getRotatingSpeedValue(),
            inputType: LifetimeParameterInputType.numberInput,
            unit: getSpeedCode(unitSystem),
          },
        ],
        [spatialTypeOfMovement.linear]: [
          {
            title: 'LIFETIME.LINEAR_STROKE_LENGTH',
            hint: t(`LIFETIME.LINEAR_STROKE_LENGTH_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear.png',
            attribute: lifetimeConfigParams.linearStrokeLength,
            attributeText: 'LIFETIME.LINEAR_STROKE_LENGTH',
            defaultValue: DefaultServiceLifeForBearings.linearStrokeLength,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getLengthCode(unitSystem),
          },
          {
            title: 'LIFETIME.LINEAR_FREQUENCY',
            hint: t(`LIFETIME.FREQUENCY_LINEAR_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear.png',
            attribute: lifetimeConfigParams.linearFrequency,
            attributeText: 'LIFETIME.LINEAR_FREQUENCY',
            defaultValue: DefaultServiceLifeForBearings.linearFrequency,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabelForUnitSystem(
              LinearFrequencyCode.STROKES_PER_MINUTE,
              getIcommerceUnitSystemName(unitSystem)
            ),
          },
          {
            title: 'LIFETIME.LINEAR_SPEED',
            attribute: lifetimeConfigParams.linearSpeed,
            attributeText: 'LIFETIME.LINEAR_SPEED',
            defaultValue: getLinearSpeedValue(),
            inputType: LifetimeParameterInputType.numberInput,
            unit: getSpeedCode(unitSystem),
            readOnly: true,
          },
        ],
        [spatialTypeOfMovement.pivoting]: [
          {
            title: 'LIFETIME.PIVOTING_ANGLE',
            hint: t(`LIFETIME.PIVOTING_ANGLE_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/pivoting.png',
            attribute: lifetimeConfigParams.pivotingAngle,
            attributeText: 'LIFETIME.PIVOTING_ANGLE',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(AngleCode.DEGREE),
          },
          {
            title: 'LIFETIME.PIVOTING_FREQUENCY',
            hint: t(`LIFETIME.FREQUENCY_PIVOTING_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/pivoting.png',
            attribute: lifetimeConfigParams.pivotingFrequency,
            attributeText: 'LIFETIME.PIVOTING_FREQUENCY',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(RotationalFrequencyCode.REVOLUTION_PER_MINUTE),
          },
          {
            title: 'LIFETIME.PIVOTING_SPEED',
            attribute: lifetimeConfigParams.pivotingSpeed,
            attributeText: 'LIFETIME.PIVOTING_SPEED',
            defaultValue: getPivotingSpeedValue(),
            inputType: LifetimeParameterInputType.numberInput,
            unit: getSpeedCode(unitSystem),
            readOnly: true,
          },
        ],
        [spatialTypeOfMovement.linearAndRotation]: [
          {
            title: 'LIFETIME.CONNECTION_OF_THE_MOVEMENTS',
            attribute: lifetimeConfigParams.temporalTypeOfMovement,
            attributeText: 'LIFETIME.TYPE_OF_OPERATION',
            defaultValue: DefaultServiceLifeForBearings.temporalTypeOfMovement,
            inputType: LifetimeParameterInputType.buttonGroup,
            buttonGroupDefinition: temporalMovementButtonGroupDefinition,
          },
          {
            title: 'LIFETIME.LINEAR_STROKE_LENGTH',
            hint: t(`LIFETIME.LINEAR_STROKE_LENGTH_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear-and-rotation.png',
            attribute: lifetimeConfigParams.linearStrokeLength,
            attributeText: 'LIFETIME.LINEAR_STROKE_LENGTH',
            defaultValue: DefaultServiceLifeForBearings.linearStrokeLength,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getLengthCode(unitSystem),
          },
          {
            title: 'LIFETIME.LINEAR_FREQUENCY',
            hint: t(`LIFETIME.FREQUENCY_LINEAR_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear-and-rotation.png',
            attribute: lifetimeConfigParams.linearFrequency,
            attributeText: 'LIFETIME.LINEAR_FREQUENCY',
            defaultValue: DefaultServiceLifeForBearings.linearFrequency,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabelForUnitSystem(
              LinearFrequencyCode.REVOLUTION_PER_MINUTE,
              getIcommerceUnitSystemName(unitSystem)
            ),
          },
          {
            title: 'LIFETIME.LINEAR_SPEED',
            attribute: lifetimeConfigParams.linearSpeed,
            attributeText: 'LIFETIME.LINEAR_SPEED',
            defaultValue: getLinearSpeedValue(),
            inputType: LifetimeParameterInputType.numberInput,
            unit: getSpeedCode(unitSystem),
            readOnly: true,
          },
          {
            title: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            hint: t(`LIFETIME.ROTATIONAL_REVOLUTIONS_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear-and-rotation.png',
            attribute: lifetimeConfigParams.rotationRevolutions,
            attributeText: 'LIFETIME.ROTATIONAL_REVOLUTIONS',
            defaultValue: DefaultServiceLifeForBearings.typeOfMovement,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(RotationalFrequencyCode.REVOLUTION_PER_MINUTE),
          },
          {
            title: 'LIFETIME.ROTATING_SPEED',
            attribute: lifetimeConfigParams.rotatingSpeed,
            attributeText: 'LIFETIME.ROTATING_SPEED',
            defaultValue: getRotatingSpeedValue(),
            inputType: LifetimeParameterInputType.numberInput,
            unit: getSpeedCode(unitSystem),
          },
          {
            title: 'LIFETIME.TEMPORAL_LINEAR_PART',
            attribute: lifetimeConfigParams.temporalLinearPart,
            attributeText: 'LIFETIME.TEMPORAL_LINEAR_PART',
            defaultValue: DefaultServiceLifeForBearings.temporalLinearPart,
            inputType: LifetimeParameterInputType.numberInput,
            unit: null,
          },
        ],
        [spatialTypeOfMovement.linearAndPivoting]: [
          {
            title: 'LIFETIME.CONNECTION_OF_THE_MOVEMENTS',
            attribute: lifetimeConfigParams.temporalTypeOfMovement,
            attributeText: 'LIFETIME.TYPE_OF_OPERATION',
            defaultValue: DefaultServiceLifeForBearings.temporalTypeOfMovement,
            inputType: LifetimeParameterInputType.buttonGroup,
            buttonGroupDefinition: temporalMovementButtonGroupDefinition,
          },
          {
            title: 'LIFETIME.LINEAR_STROKE_LENGTH',
            hint: t(`LIFETIME.LINEAR_STROKE_LENGTH_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear-and-pivoting.png',
            attribute: lifetimeConfigParams.linearStrokeLength,
            attributeText: 'LIFETIME.LINEAR_STROKE_LENGTH',
            defaultValue: DefaultServiceLifeForBearings.linearStrokeLength,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getLengthCode(unitSystem),
          },
          {
            title: 'LIFETIME.LINEAR_FREQUENCY',
            hint: t(`LIFETIME.FREQUENCY_LINEAR_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear-and-pivoting.png',
            attribute: lifetimeConfigParams.linearFrequency,
            attributeText: 'LIFETIME.LINEAR_FREQUENCY',
            defaultValue: DefaultServiceLifeForBearings.linearFrequency,
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabelForUnitSystem(
              LinearFrequencyCode.REVOLUTION_PER_MINUTE,
              getIcommerceUnitSystemName(unitSystem)
            ),
          },
          {
            title: 'LIFETIME.LINEAR_SPEED',
            attribute: lifetimeConfigParams.linearSpeed,
            attributeText: 'LIFETIME.LINEAR_SPEED',
            defaultValue: getLinearSpeedValue(),
            inputType: LifetimeParameterInputType.numberInput,
            unit: getSpeedCode(unitSystem),
            readOnly: true,
          },
          {
            title: 'LIFETIME.PIVOTING_ANGLE',
            hint: t(`LIFETIME.PIVOTING_ANGLE_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear-and-pivoting.png',
            attribute: lifetimeConfigParams.pivotingAngle,
            attributeText: 'LIFETIME.PIVOTING_ANGLE',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(AngleCode.DEGREE),
          },
          {
            title: 'LIFETIME.PIVOTING_FREQUENCY',
            hint: t(`LIFETIME.FREQUENCY_PIVOTING_HINT`, {
              ns: 'bearing-hub/bearingHub',
            }),
            hintImage: '/_hub/images/common/lifetime/linear-and-pivoting.png',
            attribute: lifetimeConfigParams.pivotingFrequency,
            attributeText: 'LIFETIME.PIVOTING_FREQUENCY',
            defaultValue: '',
            inputType: LifetimeParameterInputType.numberInput,
            unit: getUnitLabel(RotationalFrequencyCode.REVOLUTION_PER_MINUTE),
          },
          {
            title: 'LIFETIME.PIVOTING_SPEED',
            attribute: lifetimeConfigParams.pivotingSpeed,
            attributeText: 'LIFETIME.PIVOTING_SPEED',
            defaultValue: getPivotingSpeedValue(),
            inputType: LifetimeParameterInputType.numberInput,
            unit: getSpeedCode(unitSystem),
            readOnly: true,
          },
          {
            title: 'LIFETIME.TEMPORAL_LINEAR_PART',
            attribute: lifetimeConfigParams.temporalLinearPart,
            attributeText: 'LIFETIME.TEMPORAL_LINEAR_PART',
            defaultValue: DefaultServiceLifeForBearings.temporalLinearPart,
            inputType: LifetimeParameterInputType.numberInput,
            unit: null,
          },
        ],
      },
    },
  ];
};
